// Cross-Role Permissions summary (iter248).
// Read-only overview of every user who has been granted permissions
// beyond their base role's default. Admin uses this to spot cross-role
// combinations at a glance without opening each user's toggle grid.
//
// Data source: GET /api/permissions/cross-role
//   → { data: [{ user_id, name, email, base_role, extras:[{module, action}], extras_count }], total }
import React, { useEffect, useState, useCallback } from 'react';
import { Layers, RefreshCw, ChevronDown, ChevronRight, Search } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MODULE_LABEL = {
  dashboard: 'Dashboard', leads: 'Accounts', leadAnalytics: 'Account Analytics',
  tasks: 'Tasks', workflows: 'Workflows', reports: 'Reports',
  callHistory: 'Call History', callAnalytics: 'Call Analytics', callToppers: 'Call Toppers',
  properties: 'Properties', hotelLifecycle: 'Hotel Lifecycle',
  inbox: 'Inbox', campaigns: 'Campaigns', broadcast: 'Broadcast',
  chatbot: 'Chatbot', whatsappAnalytics: 'WhatsApp Analytics',
  auditHistory: 'Audit History', userManagement: 'User Management',
  settings: 'Settings', otaManagement: 'OTA Management',
  performanceInsights: 'Performance Insights',
  insights: 'Insights',
  insights_gtm: 'Insights — GTM',
  insights_cx: 'Insights — CX',
  insights_ota: 'Insights — OTA',
  insights_bfr: 'Insights — BFR',
  insights_qc_owner: 'Insights — QC/Owner',
  insights_executive: 'Insights — Executive',
  insights_onboarding_form: 'Insights — Onboarding Form',
};

const ACTION_TONE = {
  view: 'bg-sky-50 text-sky-700 border-sky-200',
  edit: 'bg-amber-50 text-amber-700 border-amber-200',
  create: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  delete: 'bg-red-50 text-red-700 border-red-200',
  action: 'bg-violet-50 text-violet-700 border-violet-200',
};

const roleLabel = (r) => {
  if (!r) return '—';
  return r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Compact chip grouping: one chip per (module) grouping its granted actions
const groupExtras = (extras) => {
  const map = new Map();
  (extras || []).forEach(({ module, action }) => {
    if (!map.has(module)) map.set(module, []);
    map.get(module).push(action);
  });
  return Array.from(map.entries()).map(([module, actions]) => ({
    module,
    actions: actions.sort(),
  }));
};

const CrossRolePermissions = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/permissions/cross-role`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setRows(Array.isArray(data?.data) ? data.data : []);
      setTotal(data?.total || 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.base_role || '').toLowerCase().includes(q) ||
        (r.extras || []).some(e => (e.module + ' ' + e.action).toLowerCase().includes(q)))
    : rows;

  const toggleRow = (id) => {
    const next = new Set(expandedRows);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpandedRows(next);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="cross-role-permissions">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between p-5 border-b border-gray-100"
        data-testid="cross-role-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
            <Layers size={18} />
          </div>
          <div className="text-left">
            <h2 className="text-lg font-bold text-gray-800">Cross-Role Permissions</h2>
            <p className="text-xs text-gray-500">
              {loading ? 'Loading…' : total === 0 ? 'No users with permissions beyond their base role' : `${total} user${total === 1 ? '' : 's'} with extra permissions granted`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); load(); }}
            className="p-2 rounded-lg hover:bg-gray-50 text-gray-500"
            title="Refresh"
            data-testid="cross-role-refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          {expanded ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="p-5 pt-3">
          <div className="mb-3 relative max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search user, role, or permission…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-400 focus:border-violet-400 outline-none"
              data-testid="cross-role-search"
            />
          </div>

          {filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400" data-testid="cross-role-empty">
              {loading ? 'Loading users…' : 'No users match. Every non-admin user is currently using only their base role default.'}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium w-56">User</th>
                    <th className="text-left px-4 py-2 font-medium w-40">Base Role</th>
                    <th className="text-left px-4 py-2 font-medium">Extra Permissions Granted</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const groups = groupExtras(r.extras);
                    const isRowExpanded = expandedRows.has(r.user_id);
                    const showGroups = isRowExpanded ? groups : groups.slice(0, 4);
                    const hidden = groups.length - showGroups.length;
                    return (
                      <tr key={r.user_id} className="border-t border-gray-100 hover:bg-gray-50/50" data-testid={`cross-role-row-${r.user_id}`}>
                        <td className="px-4 py-3 align-top">
                          <div className="font-medium text-gray-800 text-sm">{r.name}</div>
                          <div className="text-[11px] text-gray-400 mt-0.5">{r.email}</div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
                            {roleLabel(r.base_role)}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex flex-wrap gap-1.5">
                            {showGroups.map(g => (
                              <div key={g.module} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border bg-gray-50 border-gray-200 text-[11px] font-medium text-gray-700">
                                <span>{MODULE_LABEL[g.module] || g.module}</span>
                                <span className="flex items-center gap-1">
                                  {g.actions.map(a => (
                                    <span key={a} className={`inline-flex items-center px-1.5 rounded border text-[10px] ${ACTION_TONE[a] || 'bg-gray-50 border-gray-200 text-gray-600'}`}>{a}</span>
                                  ))}
                                </span>
                              </div>
                            ))}
                            {hidden > 0 && (
                              <button type="button" onClick={() => toggleRow(r.user_id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-dashed border-gray-300 text-[11px] font-medium text-gray-500 hover:bg-gray-50" data-testid={`cross-role-more-${r.user_id}`}>
                                +{hidden} more
                              </button>
                            )}
                            {isRowExpanded && groups.length > 4 && (
                              <button type="button" onClick={() => toggleRow(r.user_id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-dashed border-gray-300 text-[11px] font-medium text-gray-500 hover:bg-gray-50">
                                Show less
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CrossRolePermissions;
